import React from 'react';
import { BadgeShieldIcon, LockIcon } from '../shared/RelicVaultIcons';
import { COLORS } from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { FONTS } from 'style/typography';

/**
 * One badge in the grid, on the web (the mobile app keeps BadgeCard.js). Owner 12 Oct #6-8 and Sol owner-r24 Q3:
 * - locked: the silhouette stays, its detail is hidden - desaturated under a dark lime-tinted veil - with the padlock;
 *   hovering (or focusing) it opens the detail popup's facts in a card exactly over that badge, centred on it, never over
 *   a neighbour (owner 13 Oct: "upon hover, it should open and cover its ownself with centered and not other"; Sol
 *   owner-r27 Q1, approved on the localhost demo): rarity, name, HOW TO EARN IT and the requirement;
 * - earned: hovering (or focusing) it plays its earned moment inline - the rays, sparks and pop of "Badge earned";
 * - a click still opens the detail view for both; tap and keyboard focus show the same as hover (Sol owner-r24-dev-check);
 *   reduced motion keeps a static highlight.
 */
const CSS = `
.qlb { position: relative; display: grid; place-items: center; padding: 0; border: 0; background: none; cursor: pointer; border-radius: 16px; outline: none; transition: transform .12s; font-family: ${FONTS.body}; }
.qlb:hover, .qlb:focus-visible { transform: translateY(-2px); }
.qlb:focus-visible { box-shadow: 0 0 0 2px #c5e836; }
.qlb-glow { position: absolute; inset: 0; border-radius: 999px; }
.qlb-art { position: relative; width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(0 4px 8px rgba(0,0,0,.45)); transition: filter .25s, transform .25s; }
.qlb.locked .qlb-art { filter: grayscale(.85) brightness(.5) contrast(.9); }
.qlb-veil { position: absolute; inset: 8%; border-radius: 999px; background: radial-gradient(circle at 50% 40%, rgba(197,232,54,.10), rgba(8,10,4,.62) 70%); pointer-events: none; transition: opacity .2s; }
.qlb-lock { position: absolute; width: 34%; aspect-ratio: 1; border-radius: 999px; display: grid; place-items: center; background: rgba(6,5,18,.82); border: 1.5px solid rgba(255,216,115,.55); box-shadow: 0 2px 10px rgba(0,0,0,.5); transition: transform .2s, opacity .2s; }
.qlb-how { position: absolute; inset: 0; z-index: 2; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 12px; border-radius: 16px; border: 2px solid rgba(255,255,255,.14); background: linear-gradient(180deg, rgba(54,42,110,.97), rgba(18,15,42,.97)); box-shadow: 0 18px 40px rgba(0,0,0,.5); color: #f5f1e4; text-align: center; opacity: 0; transform: scale(.96); transition: opacity .14s, transform .14s; pointer-events: none; }
.qlb.locked:hover .qlb-how, .qlb.locked:focus .qlb-how, .qlb.locked:active .qlb-how { opacity: 1; transform: none; transition-delay: .12s; }
.qlb-rar { border: 2px solid; border-radius: 999px; padding: 2px 10px; font-size: 14px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; }
.qlb-name { font-size: 16px; font-weight: 700; line-height: 1.25; }
.qlb-howto { font-size: 14px; font-weight: 700; color: #c5e836; }
.qlb-req { font-size: 14px; font-weight: 600; line-height: 1.3; }
.qlb-rays { position: absolute; inset: -18%; border-radius: 999px; background: repeating-conic-gradient(from 0deg, rgba(252,211,77,.22) 0deg 10deg, transparent 10deg 30deg); -webkit-mask: radial-gradient(circle, #000 30%, transparent 70%); mask: radial-gradient(circle, #000 30%, transparent 70%); opacity: 0; pointer-events: none; }
.qlb-spark { position: absolute; width: 7px; height: 7px; border-radius: 2px; background: #fcd34d; transform: rotate(45deg) scale(0); opacity: 0; pointer-events: none; }
.qlb.earned:hover .qlb-rays, .qlb.earned:focus .qlb-rays, .qlb.earned:active .qlb-rays { opacity: 1; animation: qlbSpin 6s linear infinite; }
.qlb.earned:hover .qlb-art, .qlb.earned:focus .qlb-art, .qlb.earned:active .qlb-art { animation: qlbPop .9s cubic-bezier(.2,1.4,.4,1) both; filter: drop-shadow(0 0 14px rgba(252,211,77,.55)); }
.qlb.earned:hover .qlb-spark, .qlb.earned:focus .qlb-spark, .qlb.earned:active .qlb-spark { animation: qlbSpark 1.1s ease-out infinite; }
@keyframes qlbSpin { to { transform: rotate(360deg); } }
@keyframes qlbPop { 0% { transform: scale(1); } 35% { transform: scale(1.12); } 100% { transform: scale(1.04); } }
@keyframes qlbSpark { 0% { opacity: 0; transform: rotate(45deg) scale(0); } 30% { opacity: 1; transform: rotate(45deg) scale(1); } 100% { opacity: 0; transform: rotate(45deg) translate(var(--dx), var(--dy)) scale(.4); } }
/* reduced motion: a static highlight - the glow and still rays, no spin, pop or sparks (Sol owner-r24-dev-check) */
@media (prefers-reduced-motion: reduce) { .qlb.earned:hover .qlb-rays, .qlb.earned:focus .qlb-rays, .qlb.earned:active .qlb-rays, .qlb.earned:hover .qlb-art, .qlb.earned:focus .qlb-art, .qlb.earned:active .qlb-art { animation: none; } .qlb.earned .qlb-spark { display: none; } }
`;

let cssAdded = false;
const addCss = () => {
  if (cssAdded || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-ql', 'badge-card');
  style.textContent = CSS;
  document.head.appendChild(style);
  cssAdded = true;
};

// sparks around the badge: where each starts (percent of the tile) and where it drifts
const SPARKS = [
  [18, 22, '-10px', '-12px', 0], [80, 20, '12px', '-10px', 0.15], [86, 70, '12px', '10px', 0.3],
  [14, 76, '-12px', '10px', 0.45], [50, 6, '0px', '-14px', 0.6],
];

const BadgeCard = ({ badge, owned, rarityColor, size, onSelect }) => {
  addCss();
  const imageUrl = badge.imageUrl;
  const how = String(badge.requirementText || '').trim();
  return (
    <button
      type="button"
      className={`qlb ${owned ? 'earned' : 'locked'}`}
      style={{ width: size, height: size }}
      onClick={() => onSelect(badge.badgeId)}
      aria-label={owned ? badge.name : `${badge.name}, locked${how ? `. Earn by: ${how}` : ''}`}>
      <span
        className="qlb-glow"
        style={{ background: imageUrl && owned ? iconGlowFor(rarityColor) : 'rgba(255,255,255,0.03)' }}
      />
      {owned && imageUrl ? <span className="qlb-rays" /> : null}
      {imageUrl ? (
        <img className="qlb-art" src={imageUrl} alt="" draggable={false} />
      ) : (
        <BadgeShieldIcon size={Math.round(size * 0.26)} color="rgba(201,193,240,0.45)" />
      )}
      {owned && imageUrl
        ? SPARKS.map(([x, y, dx, dy, delay]) => (
            <span
              key={`${x}-${y}`}
              className="qlb-spark"
              style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${delay}s`, '--dx': dx, '--dy': dy }}
            />
          ))
        : null}
      {!owned && imageUrl ? <span className="qlb-veil" /> : null}
      {!owned && imageUrl ? (
        <span className="qlb-lock">
          <LockIcon size={Math.round(size * 0.2)} color={COLORS.gold} strokeWidth="2.6" />
        </span>
      ) : null}
      {!owned ? (
        <span className="qlb-how" aria-hidden="true">
          <span className="qlb-rar" style={{ color: rarityColor || '#4ADE80', borderColor: rarityColor || '#4ADE80' }}>
            {`${badge.rarity || 'common'} Badge`}
          </span>
          <span className="qlb-name">{badge.name}</span>
          {how ? <span className="qlb-howto">How to Earn It</span> : null}
          {how ? <span className="qlb-req">{how}</span> : null}
        </span>
      ) : null}
    </button>
  );
};

export default React.memo(BadgeCard);
